import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import { IconArrowLeft, IconChevronRight, IconPause, IconPlay } from "../components/icons";
import { MissionForm, type MissionFormValues } from "../components/mission-form";
import { Button, Card, cx, EmptyState, ErrorNotice, Loading, Notice, Textarea } from "../components/ui";
import { useMachines, useSettings } from "../lib/agent";
import { api, ApiError, errorText, type ApiBody } from "../lib/api";
import { cadenceLabel, fmtDateTime, isRunActive, machineTargetLabel, runPlainLine } from "../lib/format";
import { useNow, usePageTitle } from "../lib/hooks";
import { invalidate } from "../lib/store";
import { missionLine, useMissions, useRuns } from "./missions";

/**
 * /missions/$id — one mission in plain words: its goal, what it is up to, its recent runs, and the controls (run now,
 * pause or resume, change, stop). Frequency, computer, dates and the agent's notes sit behind « Détails techniques ».
 */
export function MissionPage({ id }: { id: string }) {
  return <MissionDetail id={id} />;
}

/** The mission itself; `embedded`: the detail column of /missions on a wide screen (Missions.dc.html). */
export function MissionDetail({ id, embedded = false }: { id: string; embedded?: boolean }) {
  const navigate = useNavigate();
  const missions = useMissions();
  const runs = useRuns();
  const machines = useMachines();
  const settings = useSettings();
  const agentName = settings.data?.agentName ?? "Ton agent";
  const now = useNow(60_000);
  const mission = missions.data?.find((m) => m.id === id);
  usePageTitle(embedded ? "Missions" : (mission?.title ?? "Mission"));
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<"run" | "status" | "notes" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const notesId = useId();

  useEffect(() => {
    if (mission) setNotes(mission.notes ?? "");
    // Only when another mission is shown: local edits must survive background refreshes.
  }, [mission?.id]);

  if (missions.loading) return <Loading />;
  if (!missions.data) return <ErrorNotice message={missions.error?.message ?? "Impossible de charger la mission."} onRetry={() => void missions.reload()} />;
  if (!mission) {
    return (
      <EmptyState icon="🔎" title="Mission introuvable" action={<BackLink />}>
        Elle a peut-être été supprimée.
      </EmptyState>
    );
  }

  const missionRuns = (runs.data ?? []).filter((r) => r.missionId === mission.id);
  const activeRun = missionRuns.find((r) => isRunActive(r.status));
  const finished = mission.status === "done" || mission.status === "cancelled";
  const working = activeRun?.status === "running" || activeRun?.status === "queued";

  async function patch(body: ApiBody<"PATCH /api/missions/:id">, what: "status" | "notes") {
    setBusy(what);
    setError(null);
    setSaved(null);
    try {
      await api("PATCH /api/missions/:id", { params: { id }, body });
      invalidate("missions");
      if (what === "notes") setSaved("Notes enregistrées.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function save(values: MissionFormValues) {
    try {
      await api("PATCH /api/missions/:id", {
        params: { id },
        body: { title: values.title, goal: values.goal, cadenceMinutes: values.cadenceMinutes, machine: values.machine },
      });
      invalidate("missions");
      setEditing(false);
      setSaved("Mission mise à jour.");
    } catch (err) {
      throw new Error(errorText(err));
    }
  }

  async function runNow() {
    setBusy("run");
    setError(null);
    try {
      const run = await api("POST /api/missions/:id/run", { params: { id }, body: {} });
      invalidate("runs");
      await navigate({ to: "/runs/$id", params: { id: run.id } });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? "Elle tourne déjà." : errorText(err));
    } finally {
      setBusy(null);
    }
  }

  const Title = embedded ? "h2" : "h1";
  return (
    <div className={embedded ? "flex min-w-0 flex-col gap-[22px] rounded-3xl border border-line bg-elev p-7" : "mx-auto max-w-2xl space-y-7"}>
      <div>
        {embedded ? <p className="text-[13px] text-muted">Demandée {fmtDateTime(mission.createdAt, now, { spoken: true })}</p> : <BackLink />}
        <Title
          className={cx(
            "font-display font-normal tracking-[-0.02em] wrap-anywhere",
            embedded ? "mt-1.5 text-[30px] leading-[1.15] font-medium tracking-[-0.01em]" : "mt-3 text-[32px] leading-[1.08] sm:text-[44px]",
          )}
        >
          {embedded ? (
            <Link to="/missions/$id" params={{ id: mission.id }} className="hover:underline">
              {mission.title}
            </Link>
          ) : (
            mission.title
          )}
        </Title>
        <p className={cx("mt-2 text-[15px] font-medium", activeRun?.status === "waiting_user" || activeRun?.status === "waiting_approval" ? "text-warning" : "text-muted")}>
          {missionLine(mission, missionRuns, agentName, now)}
        </p>
        {working ? <span aria-hidden="true" className="progress-glide mt-3 block max-w-xs" /> : null}
      </div>

      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : saved ? (
        <Notice tone="success" role="status">
          {saved}
        </Notice>
      ) : null}

      {editing ? (
        <Card className="p-5">
          <MissionForm
            initial={{ title: mission.title, goal: mission.goal, cadenceMinutes: mission.cadenceMinutes, machine: mission.machine, runNow: false }}
            machines={machines.data}
            submitLabel="Enregistrer la mission"
            onSubmit={save}
            onCancel={() => setEditing(false)}
          />
        </Card>
      ) : (
        <>
          <p className="line-clamp-4 font-display text-[18px] leading-[1.45] whitespace-pre-line text-muted italic wrap-anywhere">« {mission.goal} »</p>

          {activeRun ? (
            <Link to="/runs/$id" params={{ id: activeRun.id }} className="flex items-center gap-4 rounded-[18px] bg-sunken p-3 transition hover:bg-hover">
              <span aria-hidden="true" className="flex h-[72px] w-[120px] shrink-0 flex-col overflow-hidden rounded-[10px] bg-[#23211c] max-sm:hidden">
                <span className="h-3 bg-[#3a352e]" />
                <span className="m-1.5 flex flex-1 flex-col gap-1 rounded bg-[#f7f3ec] p-1.5">
                  <span className="h-1 w-3/5 rounded-sm bg-[#e6dccd]" />
                  <span className="h-1 w-4/5 rounded-sm bg-[#e6dccd]" />
                  <span className="h-1 w-2/5 rounded-sm bg-[#a9552f]" />
                </span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold">Regarder {agentName} travailler</span>
                <span className="mt-0.5 block text-[13.5px] text-muted">Suivre en direct</span>
              </span>
              <IconChevronRight width={18} height={18} className="shrink-0 text-muted" />
            </Link>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {activeRun ? null : !finished ? (
              <Button busy={busy === "run"} disabled={busy !== null} onClick={() => void runNow()}>
                <IconPlay width={14} height={14} />
                Lancer maintenant
              </Button>
            ) : null}
            {!finished ? (
              <Button
                variant="secondary"
                busy={busy === "status"}
                disabled={busy !== null}
                onClick={() => void patch({ status: mission.status === "paused" ? "active" : "paused" }, "status")}
              >
                {mission.status === "paused" ? <IconPlay width={14} height={14} /> : <IconPause width={14} height={14} />}
                {mission.status === "paused" ? "Reprendre" : "Mettre en pause"}
              </Button>
            ) : (
              <Button variant="secondary" busy={busy === "status"} disabled={busy !== null} onClick={() => void patch({ status: "active" }, "status")}>
                Réactiver
              </Button>
            )}
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Modifier
            </Button>
            {!finished ? (
              <Button variant="ghost" disabled={busy !== null} onClick={() => void patch({ status: "cancelled" }, "status")}>
                Arrêter
              </Button>
            ) : null}
          </div>
        </>
      )}

      {missionRuns.length ? (
        <section aria-labelledby={`mission-runs-${mission.id}`}>
          <h2 id={`mission-runs-${mission.id}`} className="mb-2 px-1 text-[13px] font-semibold tracking-[0.04em] text-muted uppercase">
            Dernières fois
          </h2>
          <ul className={cx("flex flex-col divide-y divide-sunken rounded-[18px]", embedded ? "bg-bg" : "border border-line bg-elev")}>
            {missionRuns.slice(0, 5).map((r) => (
              <li key={r.id}>
                <Link to="/runs/$id" params={{ id: r.id }} className="flex min-h-12 items-center gap-3 px-4 py-3 hover:bg-sunken">
                  <span className="min-w-0 flex-1 truncate text-[14.5px]">{runPlainLine(r, agentName, now)}</span>
                  <IconChevronRight width={16} height={16} className="shrink-0 text-muted" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <details className="group">
        <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1 text-[14px] font-medium text-muted select-none hover:text-fg [&::-webkit-details-marker]:hidden">
          <span>Détails techniques</span>
          <span aria-hidden="true" className="text-[11px] transition group-open:rotate-90">
            ›
          </span>
        </summary>
        <div className="mt-3 space-y-5 rounded-[18px] bg-sunken p-4">
          <dl className="grid gap-x-6 gap-y-1.5 text-[13.5px] sm:grid-cols-[auto_1fr]">
            <dt className="text-subtle">Fréquence</dt>
            <dd>{cadenceLabel(mission.cadenceMinutes)}</dd>
            <dt className="text-subtle">Machine</dt>
            <dd>{machineTargetLabel(mission.machine, machines.data)}</dd>
            <dt className="text-subtle">Prochaine</dt>
            <dd>{mission.status === "active" && mission.nextRunAt ? fmtDateTime(mission.nextRunAt, now) : "À la demande"}</dd>
            <dt className="text-subtle">Créée</dt>
            <dd>{fmtDateTime(mission.createdAt, now)}</dd>
          </dl>
          <div>
            <label htmlFor={notesId} className="mb-1 block text-[13px] font-medium text-muted">
              Notes de la mission (ce que {agentName} retient d'une fois à l'autre)
            </label>
            <Textarea id={notesId} value={notes} maxLength={8000} onChange={(e) => setNotes(e.target.value)} className="min-h-28" />
            <div className="mt-2">
              <Button variant="secondary" size="sm" busy={busy === "notes"} disabled={busy !== null || notes === (mission.notes ?? "")} onClick={() => void patch({ notes }, "notes")}>
                Enregistrer les notes
              </Button>
            </div>
          </div>
        </div>
      </details>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/missions" className="inline-flex min-h-11 items-center gap-1.5 text-[13px] font-medium text-muted hover:text-fg">
      <IconArrowLeft width={15} height={15} />
      Retour
    </Link>
  );
}
